import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";
import { COMPANY, SERVICE_OPTIONS } from "../../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: SERVICE_OPTIONS[0],
  location: "",
  project_details: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name || !form.email || !form.service || !form.project_details) {
      toast.error("Please fill in name, email, service, and project details.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSuccess(true);
      toast.success("Quote request sent. We'll reach out within one business day.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your request. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative py-24 md:py-32 bg-[#05070F] overflow-hidden"
    >
      <Toaster theme="dark" richColors position="top-center" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(61,192,175,0.18),transparent_60%)]" />
        <div className="absolute inset-0 tb-grid-dense opacity-30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
            / REQUEST A QUOTE
          </div>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter leading-[0.95]">
            Tell us about the project.
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            Share a few details and we'll come back with a clear scope, timeline,
            and price — usually within one business day.
          </p>

          <div className="mt-10 space-y-5 text-sm">
            <a
              href={COMPANY.phoneHref}
              data-testid="contact-phone"
              className="flex items-start gap-4 text-zinc-300 hover:text-white transition-colors"
            >
              <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 border border-white/10">
                <Phone className="h-4 w-4 text-[#5EE3CE]" />
              </span>
              <div>
                <div className="text-zinc-500 text-xs tracking-[0.2em] uppercase font-mono">
                  Call us
                </div>
                <div className="font-display text-lg font-bold">{COMPANY.phone}</div>
              </div>
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="contact-email"
              className="flex items-start gap-4 text-zinc-300 hover:text-white transition-colors"
            >
              <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 border border-white/10">
                <Mail className="h-4 w-4 text-[#5EE3CE]" />
              </span>
              <div>
                <div className="text-zinc-500 text-xs tracking-[0.2em] uppercase font-mono">
                  Email
                </div>
                <div className="font-display text-lg font-bold">{COMPANY.email}</div>
              </div>
            </a>
            <div className="flex items-start gap-4 text-zinc-300">
              <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 border border-white/10">
                <MapPin className="h-4 w-4 text-[#5EE3CE]" />
              </span>
              <div>
                <div className="text-zinc-500 text-xs tracking-[0.2em] uppercase font-mono">
                  Office
                </div>
                <div className="font-display text-base font-bold">
                  {COMPANY.address}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="lg:col-span-7">
          <motion.form
            onSubmit={onSubmit}
            data-testid="quote-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl p-6 md:p-10 tb-glow"
          >
            {success && (
              <div
                data-testid="quote-success"
                className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200"
              >
                <Check className="h-4 w-4" />
                Thanks — your request is in. We'll be in touch shortly.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name" required>
                <input
                  type="text"
                  required
                  data-testid="quote-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="John Carter"
                  className={inputCls}
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  required
                  data-testid="quote-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  data-testid="quote-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(431) 000-0000"
                  className={inputCls}
                />
              </Field>
              <Field label="Company">
                <input
                  type="text"
                  data-testid="quote-company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Optional"
                  className={inputCls}
                />
              </Field>
              <Field label="Service needed" required>
                <select
                  required
                  data-testid="quote-service"
                  value={form.service}
                  onChange={update("service")}
                  className={`${inputCls} appearance-none pr-10`}
                >
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-black">
                      {o}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Project location">
                <input
                  type="text"
                  data-testid="quote-location"
                  value={form.location}
                  onChange={update("location")}
                  placeholder="City / site address"
                  className={inputCls}
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Project details" required>
                  <textarea
                    required
                    rows={5}
                    data-testid="quote-details"
                    value={form.project_details}
                    onChange={update("project_details")}
                    placeholder="Site size, deliverables needed, timeline, any constraints..."
                    className={`${inputCls} resize-none`}
                  />
                </Field>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs text-zinc-500 max-w-sm">
                By submitting, you agree to be contacted about your project. We
                never share your information.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit"
                className="group inline-flex items-center gap-3 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] disabled:opacity-60 text-white px-7 py-3.5 text-sm font-medium transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send request
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full rounded-lg bg-zinc-900/80 border border-white/10 text-white placeholder:text-zinc-600 px-4 py-3 text-sm outline-none focus:border-[#3DC0AF] focus:ring-2 focus:ring-[#3DC0AF]/30 transition-all";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] tracking-[0.2em] uppercase font-mono text-zinc-500 mb-2">
      {label} {required && <span className="text-[#3DC0AF]">*</span>}
    </span>
    {children}
  </label>
);
